import { ReactNode } from "react";
import { ArrowRight } from "lucide-react";
import { cn } from "web-design-system";
import { AllocationBucket } from "creso-core/types";
import { formatAsPercentage } from "creso-core/utils";
import { ASSET_ALLOCATION_BUCKET_COLORS } from "@crm/components/overview/asset-allocation";
import { ALLOCATION_COLORS } from "../constants";

const formatPct = (value: number | string) => formatAsPercentage(value, "-", 0);

const TOP_COUNTRIES = 4;

export function bucketColor(key: string, index: number) {
  return (
    ASSET_ALLOCATION_BUCKET_COLORS[key.toLowerCase()] ??
    ALLOCATION_COLORS[index % ALLOCATION_COLORS.length]
  );
}

export function LegendItem({
  dashed,
  children,
}: {
  dashed?: boolean;
  children: ReactNode;
}) {
  return (
    <span className="flex items-center gap-2">
      <span
        className={cn(
          "w-5 border-t-4 border-muted-foreground",
          dashed && "border-dotted"
        )}
      />
      {children}
    </span>
  );
}

type MergedBucket = {
  key: string;
  label: string;
  before: number;
  after: number;
};

export function mergeBuckets(
  before: { key: string; label: string; pct: number }[],
  after: { key: string; label: string; pct: number }[]
): MergedBucket[] {
  const merged = new Map<string, MergedBucket>();
  before.forEach((b) =>
    merged.set(b.key, { key: b.key, label: b.label, before: b.pct, after: 0 })
  );
  after.forEach((b) => {
    const existing = merged.get(b.key);
    merged.set(b.key, {
      key: b.key,
      label: existing?.label ?? b.label,
      before: existing?.before ?? 0,
      after: b.pct,
    });
  });
  return [...merged.values()];
}

// Current vs after-review allocation: stacked bars for the whole portfolio,
// then a column per asset class with its sub-bucket bars.
export function AssetAllocationComparison({
  before,
  after,
}: {
  before: AllocationBucket[];
  after: AllocationBucket[];
}) {
  const buckets = mergeBuckets(before, after);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-6 text-sm text-muted-foreground">
        <LegendItem>Current portfolio</LegendItem>
        <LegendItem dashed>After review</LegendItem>
      </div>
      <div className="flex flex-col gap-8">
        <StackedBar buckets={before} />
        <StackedBar buckets={after} dashed />
      </div>
      <div className="grid grid-cols-4 gap-3">
        {buckets.map((bucket, index) => {
          const color = bucketColor(bucket.key, index);
          const merged = mergeBuckets(
            before.find((b) => b.key === bucket.key)?.sub_buckets ?? [],
            after.find((b) => b.key === bucket.key)?.sub_buckets ?? []
          );
          const subs =
            bucket.key.toLowerCase() === "global"
              ? groupTail(merged, TOP_COUNTRIES)
              : merged;
          // Sub-bucket shares are of the whole portfolio, so size their bars
          // against the bucket's own share to keep them readable.
          const scale = Math.max(bucket.before, bucket.after, 1);
          const subColor = `color-mix(in srgb, ${color} 55%, transparent)`;
          return (
            <div key={bucket.key} className="flex flex-col gap-4">
              <div className="flex items-center gap-2 rounded-lg bg-card p-4 text-sm">
                <span
                  className="size-3 shrink-0 rounded-xs"
                  style={{ background: color }}
                />
                <span className="flex-1 truncate">{bucket.label}</span>
                <span className="flex items-center gap-1">
                  {formatPct(bucket.before)}
                  <ArrowRight className="size-3" />
                  {formatPct(bucket.after)}
                </span>
              </div>
              {subs.map((sub) => (
                <div key={sub.key} className="flex flex-col gap-1 px-4">
                  <span className="text-xs">{sub.label}</span>
                  <SubBar
                    pct={sub.before}
                    width={(sub.before / scale) * 100}
                    color={subColor}
                  />
                  <SubBar
                    pct={sub.after}
                    width={(sub.after / scale) * 100}
                    color={subColor}
                    dashed
                  />
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function StackedBar({
  buckets,
  dashed,
}: {
  buckets: AllocationBucket[];
  dashed?: boolean;
}) {
  return (
    <div className={cn("flex w-full gap-2", dashed ? "h-5" : "h-4")}>
      {buckets
        .filter((bucket) => bucket.pct > 0)
        .map((bucket, index) => {
          const color = bucketColor(bucket.key, index);
          return dashed ? (
            // Dots sit on a pale track of the bucket's colour.
            <div
              key={bucket.key}
              className="flex h-full items-center rounded-full px-1.5"
              style={{
                width: `${bucket.pct}%`,
                background: `color-mix(in srgb, ${color} 15%, transparent)`,
              }}
            >
              <div
                className="h-2 w-full rounded-full"
                style={{ background: dots(color, 8) }}
              />
            </div>
          ) : (
            <div
              key={bucket.key}
              className="h-full rounded-full"
              style={{ width: `${bucket.pct}%`, background: color }}
            />
          );
        })}
    </div>
  );
}

// Solid bar for the current portfolio, dotted for after the review.
function SubBar({
  pct,
  width,
  color,
  dashed,
}: {
  pct: number;
  width: number;
  color: string;
  dashed?: boolean;
}) {
  return (
    <div className="flex h-5 items-center gap-4">
      <div className="flex-1">
        <div
          className="h-1.5 rounded-full"
          style={{
            width: `${Math.min(Math.max(width, 0), 100)}%`,
            background: dashed ? dots(color, 6) : color,
          }}
        />
      </div>
      <span className="w-12 text-right text-xs">
        {formatAsPercentage(pct, "-", 1)}
      </span>
    </div>
  );
}

// Square dots the height of the bar, with half-size gaps between them.
const dots = (color: string, size: number) =>
  `repeating-linear-gradient(90deg, ${color} 0 ${size}px, transparent ${size}px ${size * 1.5}px)`;

// Keeps the largest `count` entries (by either side) and sums the rest into
// "Others", so before and after always show the same rows.
function groupTail(subs: MergedBucket[], count: number): MergedBucket[] {
  if (subs.length <= count) return subs;
  const sorted = [...subs].sort(
    (a, b) => Math.max(b.before, b.after) - Math.max(a.before, a.after)
  );
  const rest = sorted.slice(count);
  return [
    ...sorted.slice(0, count),
    {
      key: "others",
      label: "Others",
      before: rest.reduce((sum, sub) => sum + sub.before, 0),
      after: rest.reduce((sum, sub) => sum + sub.after, 0),
    },
  ];
}
